import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from './Footer';
import { ChatPanel } from './chat/ChatPanel';
import { ChatToggleButton } from './chat/ChatToggleButton';
import { AssistantFloatingButton, AssistantPanel } from '@/modules/assistant/components/panel';
import { useChatPanel } from '@/contexts/ChatPanelContext';
import { useAuth } from '@/contexts/AuthContext';
import { Feature } from '@/contexts/FeaturesContext';
import { MarketingTopbar } from '@/modules/marketing/components/MarketingTopbar';
import { UserX, Mail, ArrowRight, Info } from 'lucide-react';

interface LayoutProps {
  children: ReactNode;
}

function ImpersonationBanner() {
  const { isImpersonating, user, sessions, activeSessionIndex, stopImpersonation } = useAuth();
  if (!isImpersonating) return null;

  const adminName = sessions[activeSessionIndex]?.impersonatedBy;

  return (
    <div className="h-10 bg-purple-600 text-white flex items-center justify-center gap-3 px-4 text-sm font-medium shadow-md">
      <span>
        Impersonating <strong>{user?.fullName}</strong> ({user?.role?.toLowerCase()})
        {adminName && <span className="font-normal opacity-80">: logged in as {adminName}</span>}
      </span>
      <button
        onClick={stopImpersonation}
        className="flex items-center gap-1 bg-white/20 hover:bg-white/30 rounded px-2 py-0.5 text-xs transition-colors"
      >
        <UserX className="h-3 w-3" />
        Stop impersonating
      </button>
    </div>
  );
}

function EmailVerificationBanner() {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !user || user.emailVerified) return null;

  if (!Feature.EMAIL.isActive()) {
    return (
      <div className="h-10 bg-blue-500 text-white flex items-center justify-center gap-3 px-4 text-sm font-medium shadow-md">
        <Info className="h-4 w-4" />
        <span>
          Email verification is coming soon. No action needed right now.
        </span>
      </div>
    );
  }

  return (
    <div className="min-h-10 py-1.5 bg-yellow-500 text-yellow-950 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 text-sm font-medium shadow-md">
      <Mail className="h-4 w-4" />
      <span>
        Your email <strong>{user.email}</strong> isn't verified yet
      </span>
      <Link 
        to="/settings"
        className="flex items-center gap-1 bg-yellow-600/30 hover:bg-yellow-600/50 rounded px-2 py-0.5 text-xs transition-colors"
      >
        Verify now
        <ArrowRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

export function Layout({ children }: LayoutProps) {
  const { isOpen } = useChatPanel();
  const { isAuthenticated } = useAuth();

  return (
    <div className={`min-h-screen page-gradient flex flex-col transition-all duration-300 ${isAuthenticated && isOpen ? 'pr-[380px]' : ''}`}>
      <div className="sticky top-0 z-50">
        <ImpersonationBanner />
        <EmailVerificationBanner />
        <MarketingTopbar />
      </div>
      <main className="container py-6 flex-1">
        {children}
      </main>
      <Footer />
      {isAuthenticated && <ChatPanel />}
      {isAuthenticated && <ChatToggleButton />}
      {isAuthenticated && <AssistantPanel />}
      {isAuthenticated && <AssistantFloatingButton />}
    </div>
  );
}
